import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
    motion,
    useMotionValue,
    useSpring,
    useMotionTemplate,
} from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { FadeUp, SectionLabel } from "@/components/Reveal";
import { SERVICES } from "@/data/services";

function ServiceCard({ service, index }) {
    const ref = useRef(null);
    const navigate = useNavigate();
    const rx = useSpring(useMotionValue(0), { stiffness: 160, damping: 20 });
    const ry = useSpring(useMotionValue(0), { stiffness: 160, damping: 20 });
    const gx = useMotionValue(0);
    const gy = useMotionValue(0);
    const glow = useMotionTemplate`radial-gradient(420px circle at ${gx}px ${gy}px, rgba(200,169,106,0.13), transparent 65%)`;
    const Icon = service.icon;

    const onMove = (e) => {
        const rect = ref.current?.getBoundingClientRect();
        if (!rect) return;
        const px = (e.clientX - rect.left) / rect.width - 0.5;
        const py = (e.clientY - rect.top) / rect.height - 0.5;
        ry.set(px * 9);
        rx.set(py * -8);
        gx.set(e.clientX - rect.left);
        gy.set(e.clientY - rect.top);
    };
    const onLeave = () => {
        rx.set(0);
        ry.set(0);
    };
    const open = () =>
        navigate(
            `/contact?type=${encodeURIComponent(service.title)}&from=services`,
        );

    return (
        <FadeUp delay={index * 0.08} className={service.span} y={60}>
            <motion.article
                ref={ref}
                data-testid={`service-card-${service.slug}`}
                onMouseMove={onMove}
                onMouseLeave={onLeave}
                onClick={open}
                style={{ rotateX: rx, rotateY: ry, transformPerspective: 1000 }}
                className="group relative flex min-h-[320px] cursor-pointer flex-col justify-between overflow-hidden rounded-2xl border border-white/[0.07] bg-graphite p-8 transition-colors duration-500 hover:border-gold/30 md:min-h-[360px]"
                data-cursor="hover"
                role="link"
                aria-label={`Start a ${service.title} enquiry`}
            >
                {service.image && (
                    <>
                        <div className="absolute inset-0 overflow-hidden">
                            <img
                                src={service.image}
                                alt={service.title}
                                loading="lazy"
                                className="h-full w-full object-cover opacity-30 transition-transform duration-[1.2s] ease-out group-hover:scale-105 group-hover:opacity-40"
                            />
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-coal via-coal/60 to-transparent" />
                    </>
                )}
                <motion.div
                    className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                    style={{ background: glow }}
                />

                <div className="relative flex items-start justify-between">
                    <span className="flex h-12 w-12 items-center justify-center border border-gold/40 text-gold transition-colors duration-500 group-hover:bg-gold group-hover:text-coal">
                        <Icon size={20} strokeWidth={1.5} />
                    </span>
                    <span className="font-display text-5xl font-black text-outline opacity-70">
                        {service.n}
                    </span>
                </div>

                <div className="relative">
                    <h3 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                        {service.title}
                    </h3>
                    <p className="mt-3 max-w-sm text-sm leading-relaxed text-ash">
                        {service.desc}
                    </p>
                    <button
                        data-testid={`service-explore-${service.slug}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            open();
                        }}
                        className="mt-6 inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-gold"
                    >
                        Start Enquiry
                        <ArrowUpRight
                            size={14}
                            className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                        />
                    </button>
                </div>
            </motion.article>
        </FadeUp>
    );
}

export default function Services() {
    return (
        <section
            id="services"
            data-testid="services-section"
            className="relative mx-auto max-w-7xl overflow-clip px-6 py-32 sm:px-10 sm:py-44"
        >
            <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
                <div>
                    <SectionLabel index="02" title="Services" />
                    <h2 className="mt-10 font-display text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
                        Five disciplines,
                        <br />
                        <span className="text-outline">one obsession.</span>
                    </h2>
                </div>
                <FadeUp delay={0.2} className="max-w-xs">
                    <p className="text-sm leading-relaxed text-ash">
                        A single senior team carries your project from first sketch
                        to final key handover. Tap a discipline to see how we work.
                    </p>
                </FadeUp>
            </div>

            <div className="mt-20 grid grid-cols-1 gap-5 md:grid-cols-6">
                {SERVICES.map((s, i) => (
                    <ServiceCard key={s.n} service={s} index={i} />
                ))}
            </div>
        </section>
    );
}
